import { observer } from "mobx-react-lite";
import { LineChart } from "@mui/x-charts/LineChart";
import React, { useCallback, useMemo } from "react";
import { ChartsReferenceLine, MarkElement, MarkElementProps } from "@mui/x-charts";
import { PricesStorage, PriceTier } from "../../services/shop/PricesStorage";
import { DatasetType } from "@mui/x-charts/internals";
import { orderStorage } from "../../services/shop/OrderStorage";
import { translationService } from "../../services/translation/TranslationService";
import { formatEuro } from "../../services/shop/formatEuro";

// Roughly how many ticks the x-axis shows; the step between them is rounded to 1, 2 or 5 x 10^n apps.
const TARGET_TICKS = 15;

const niceStep = (raw: number) => {
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  return [1, 2, 5, 10].map((m) => m * magnitude).find((step) => step >= raw) ?? 10 * magnitude;
};

export interface CoinChartProps {
  coins: number;
  pricesStorage: PricesStorage;
}

export default observer(function CoinPriceChart(props: CoinChartProps) {
  const { coins, pricesStorage } = props;
  // `closest` uses .reduce() — empty arrays throw without an initial value.
  // The axis can be empty when an activity price is 0 (e.g. VideoConversion = 0).
  const closest = (array: any[], value: number) => {
    if (!array || array.length === 0) return 0;
    return array.reduce((prev, next) =>
        Math.abs(next - value) < Math.abs(prev - value) ? next : prev
    );
  };
  const index = closest(pricesStorage.coinAxis, coins);

  const mark = useCallback(
    (props: MarkElementProps) => {
      const marked = useMemo(() => props.dataIndex === index, [props.dataIndex, index]);
      return <MarkElement {...props} shape={marked ? "cross" : "circle"} />;
    },
    [index]
  );

  const x = pricesStorage.coinAxis;
  const y = pricesStorage.currencyAxis;

  // The chart is plotted in balance cents (what the price tiers are defined in), but the shop
  // sells apps, so the axis is labelled in apps, at round app counts.
  const unitCents = orderStorage.unitCents;
  const appTicks = useMemo(() => {
    const max = x.length ? x[x.length - 1] : 0;
    if (!(max > 0) || !(unitCents > 0)) return [];
    const step = unitCents * niceStep(max / unitCents / TARGET_TICKS);
    return Array.from({ length: Math.floor(max / step) + 1 }, (_, i) => i * step);
  }, [x, unitCents]);

  const dataset: DatasetType = useMemo(
    () =>
      x.map((value: number, index: number) => {
        return { x: value, y: y[index] };
      }),
    [x, y]
  );


  return (
    <LineChart
      onHighlightChange={() => {}}
      highlightedItem={{ dataIndex: index }}
      dataset={dataset}
      xAxis={[
        {
          dataKey: "x",
          labelStyle: { transform: "translate(0, 12px)", fontWeight: "bold" },
          tickLabelStyle: { transform: "rotate(-50deg) translate(-20px)" },
          tickInterval: appTicks,
          valueFormatter: (value: number) => Math.round(value / unitCents).toLocaleString(),
          label: translationService.translate("shop.chart.x-label"),
        },
      ]}
      series={[{ dataKey: "y", color: "#1976d2", showMark: false }]}
      slots={{ mark: mark }}
      sx={{ pl: 4 }}
    >
      {/* The marker and price use the exact order, not the nearest plotted point, so the € shown
          is what the order costs. */}
      <ChartsReferenceLine x={coins} lineStyle={{ stroke: "#fdb462", strokeWidth: 4 }} />
      <ChartsReferenceLine labelAlign="end" label={formatEuro(pricesStorage.calculateCurrency(coins) * 100)} y={pricesStorage.calculateCurrency(coins)} lineStyle={{ stroke: "#fdb462", strokeWidth: 4 }} />
      {pricesStorage.prices.map((priceTier: PriceTier) => (
        <>
          {/* <ChartsReferenceLine y={priceTier.minimum} lineStyle={{ strokeDasharray: "5 5" }} /> */}
          <ChartsReferenceLine
            x={priceTier.minimum}
            lineStyle={{ strokeDasharray: "5 5" }}
            label={priceTier.minimum !== pricesStorage.prices[0].minimum ? "" + priceTier.discount + "%" : ""}
            labelAlign="start"
          />
        </>
      ))}
    </LineChart>
  );
});
